import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { meService } from "../services/me.service";
import payoutBanks from "../data/payout-banks.json";

/**
 * useMe()
 *
 * React Query layer over meService. Covers profile, KYC, recipients,
 * payment methods, and transfers — all scoped to the logged-in user.
 *
 * Query keys:
 *   ["me"]                                        — profile
 *   ["me", "kyc-profiles"]                        — KYC profiles
 *   ["me", "recipients"]                          — recipient list
 *   ["me", "recipients", recipientId]             — single recipient
 *   ["me", "recipients", recipientId, "payment-methods"] — payment methods
 *   ["me", "transfers"]                           — transfer list
 *   ["me", "transfers", transferId]               — single transfer summary
 *   ["me", "transfers", transferId, "detail"]     — full transfer detail
 *   ["me", "transfers", transferId, "funding-instructions"] — customer-safe deposit instructions
 *   ["me", "reference", "banks", country]         — payout bank list for a country
 *   ["me", "corridors"]                           — countries the customer can send to
 */
export function useMe() {
  const queryClient = useQueryClient();

  // ─── Profile ──────────────────────────────────────────────────────────────

  const profileQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => meService.getMe(),
  });

  const updateMeMutation = useMutation({
    mutationFn: (payload) => meService.updateMe(payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(["me"], updated);
    },
  });

  // ─── KYC ──────────────────────────────────────────────────────────────────

  const kycProfilesQuery = useQuery({
    queryKey: ["me", "kyc-profiles"],
    queryFn: () => meService.getKycProfiles(),
  });

  // ─── Recipients ───────────────────────────────────────────────────────────

  const recipientsQuery = useQuery({
    queryKey: ["me", "recipients"],
    queryFn: () => meService.listRecipients(),
  });

  const createRecipientMutation = useMutation({
    mutationFn: (payload) => meService.createRecipient(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me", "recipients"] });
    },
  });

  const updateRecipientMutation = useMutation({
    mutationFn: ({ recipientId, ...payload }) =>
      meService.updateRecipient(recipientId, payload),
    onSuccess: (updated) => {
      queryClient.setQueryData(["me", "recipients", updated.id], updated);
      queryClient.invalidateQueries({ queryKey: ["me", "recipients"] });
    },
  });

  const createPaymentMethodMutation = useMutation({
    mutationFn: ({ recipientId, ...payload }) =>
      meService.createPaymentMethod(recipientId, payload),
    onSuccess: (_, { recipientId }) => {
      queryClient.invalidateQueries({
        queryKey: ["me", "recipients", recipientId, "payment-methods"],
      });
      // Recipient objects carry an embedded `payment_methods` array — a new
      // default payment method changes that array, so the recipient list
      // (and any single-recipient cache) needs to refetch too.
      queryClient.invalidateQueries({ queryKey: ["me", "recipients"] });
    },
  });
  // ─── Transfers ────────────────────────────────────────────────────────────

  const transfersQuery = useQuery({
    queryKey: ["me", "transfers"],
    queryFn: () => meService.listTransfers(),
  });

  const createTransferMutation = useMutation({
    mutationFn: (payload) => meService.createTransfer(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["me", "transfers"] });
    },
  });

  const createQuoteMutation = useMutation({
    mutationFn: (transferId) => meService.createQuote(transferId),
  });

  const acceptQuoteMutation = useMutation({
    mutationFn: ({ transferId, quoteId }) =>
      meService.acceptQuote(transferId, quoteId),
    onSuccess: (updated) => {
      queryClient.setQueryData(["me", "transfers", updated.id], updated);
    },
  });

  const prepareTransferMutation = useMutation({
      mutationFn: (variables) => meService.prepareTransfer(variables.transferId, variables.pin),    
      onSuccess: (_, { transferId }) => {
      queryClient.invalidateQueries({ queryKey: ["me", "transfers", transferId, "detail"] });
    },
  });

  const confirmTransferMutation = useMutation({
    mutationFn: ({ transferId, pin }) => meService.confirmTransfer(transferId, pin),
    onSuccess: (updated, { transferId }) => {
      // Confirm returns the completed transfer — seed the cache with it
      // directly, then invalidate detail so polling picks up the final legs.
      queryClient.setQueryData(["me", "transfers", transferId], updated);
      queryClient.invalidateQueries({ queryKey: ["me", "transfers", transferId, "detail"] });
    },
  });

  return {
    // Profile
    profile: profileQuery.data ?? null,
    isLoadingProfile: profileQuery.isLoading,
    profileError: profileQuery.error,
    updateMe: updateMeMutation.mutate,
    updateMeAsync: updateMeMutation.mutateAsync,
    isUpdatingMe: updateMeMutation.isPending,
    updateMeError: updateMeMutation.error,

    // KYC
    kycProfiles: kycProfilesQuery.data ?? [],
    isLoadingKyc: kycProfilesQuery.isLoading,
    kycError: kycProfilesQuery.error,

    // Recipients
    recipients: recipientsQuery.data ?? [],
    isLoadingRecipients: recipientsQuery.isLoading,
    recipientsError: recipientsQuery.error,
    createRecipient: createRecipientMutation.mutate,
    createRecipientAsync: createRecipientMutation.mutateAsync,
    isCreatingRecipient: createRecipientMutation.isPending,
    createRecipientError: createRecipientMutation.error,
    updateRecipient: updateRecipientMutation.mutate,
    updateRecipientAsync: updateRecipientMutation.mutateAsync,
    isUpdatingRecipient: updateRecipientMutation.isPending,
    createPaymentMethod: createPaymentMethodMutation.mutate,
    createPaymentMethodAsync: createPaymentMethodMutation.mutateAsync,
    isCreatingPaymentMethod: createPaymentMethodMutation.isPending,

    // Transfers
    transfers: transfersQuery.data ?? [],
    isLoadingTransfers: transfersQuery.isLoading,
    transfersError: transfersQuery.error,
    createTransfer: createTransferMutation.mutate,
    createTransferAsync: createTransferMutation.mutateAsync,
    isCreatingTransfer: createTransferMutation.isPending,
    createTransferError: createTransferMutation.error,
    createQuote: createQuoteMutation.mutate,
    createQuoteAsync: createQuoteMutation.mutateAsync,
    isCreatingQuote: createQuoteMutation.isPending,
    createQuoteError: createQuoteMutation.error,
    acceptQuote: acceptQuoteMutation.mutate,
    acceptQuoteAsync: acceptQuoteMutation.mutateAsync,
    isAcceptingQuote: acceptQuoteMutation.isPending,
    confirmTransfer: confirmTransferMutation.mutate,
    confirmTransferAsync: confirmTransferMutation.mutateAsync,
    isConfirmingTransfer: confirmTransferMutation.isPending,
    confirmTransferError: confirmTransferMutation.error,
    prepareTransfer: prepareTransferMutation.mutate,
    prepareTransferAsync: prepareTransferMutation.mutateAsync,
    isPreparingTransfer: prepareTransferMutation.isPending,
    prepareTransferError: prepareTransferMutation.error,
  };
}

/**
 * useRecipient(recipientId)
 * Separate hook for single-recipient queries — only fetches when recipientId is provided.
 */
export function useRecipient(recipientId) {
  const recipientQuery = useQuery({
    queryKey: ["me", "recipients", recipientId],
    queryFn: () => meService.getRecipient(recipientId),
    enabled: Boolean(recipientId),
  });

  const paymentMethodsQuery = useQuery({
    queryKey: ["me", "recipients", recipientId, "payment-methods"],
    queryFn: () => meService.listPaymentMethods(recipientId),
    enabled: Boolean(recipientId),
  });

  return {
    recipient: recipientQuery.data ?? null,
    isLoadingRecipient: recipientQuery.isLoading,
    recipientError: recipientQuery.error,
    paymentMethods: paymentMethodsQuery.data ?? [],
    isLoadingPaymentMethods: paymentMethodsQuery.isLoading,
  };
}

/**
 * useTransfer(transferId)
 * Separate hook for single-transfer queries with optional detail polling.
 */
export function useTransfer(transferId, { pollDetail = false } = {}) {
  const transferQuery = useQuery({
    queryKey: ["me", "transfers", transferId],
    queryFn: () => meService.getTransfer(transferId),
    enabled: Boolean(transferId),
  });

  const transferDetailQuery = useQuery({
    queryKey: ["me", "transfers", transferId, "detail"],
    queryFn: () => meService.getTransferDetail(transferId),
    enabled: Boolean(transferId),
    refetchInterval: (query) => {
      if (!pollDetail) return false;

      const data = query.state.data;

      // Stop only when the ENTIRE transfer is terminal.
      const terminalStatuses = [
        "completed",
        "failed",
        "cancelled",
      ];

      if (terminalStatuses.includes(data?.status)) {
        return false;
      }

      // Keep polling while the transfer is still progressing.
      return 5000;
    },
  });

  return {
    transfer: transferQuery.data ?? null,
    isLoadingTransfer: transferQuery.isLoading,
    transferError: transferQuery.error,

    transferDetail: transferDetailQuery.data ?? null,
    isLoadingDetail: transferDetailQuery.isLoading,
    detailError: transferDetailQuery.error,
  };
}

/**
 * useFundingInstructions(transferId, { poll })
 *
 * Reads customer-safe deposit instructions from GET
 * /me/transfers/{id}/funding-instructions — the single-purpose,
 * provider-agnostic endpoint (as opposed to useTransfer's /detail
 * polling, which also carries routing/status/legs data unrelated to
 * "where do I send money").
 *
 * The endpoint returns 409 while instructions aren't ready yet (no
 * provider_reference on the onramp leg's provider_transaction). That's
 * an expected waiting state, not a failure — it's surfaced separately
 * via `fundingInstructionsNotReady` so callers don't render it as an
 * error banner. Any other error status is passed through as-is.
 */
export function useFundingInstructions(transferId, { poll = false } = {}) {
  const query = useQuery({
    queryKey: ["me", "transfers", transferId, "funding-instructions"],
    queryFn: () => meService.getFundingInstructions(transferId),
    enabled: Boolean(transferId),
    retry: false,
    refetchInterval: (queryState) => {
      if (!poll) return false;

      // Once instructions are ready, keep polling anyway is unnecessary —
      // the deposit itself is confirmed via provider webhook/reconciliation
      // elsewhere (see useTransfer's /detail polling), not from this
      // endpoint. Stop as soon as we have a complete response.
      if (queryState.state.data) return false;

      return 4000;
    },
  });

  const status = query.error?.response?.status;

  return {
    fundingInstructions: query.data ?? null,
    isLoadingFundingInstructions: query.isLoading,
    fundingInstructionsNotReady: status === 409,
    fundingInstructionsError: status && status !== 409 ? query.error : null,
  };
}

/**
 * usePayoutBanks(country)
 *
 * Reads GET /me/reference/banks?country={country} for populating a bank
 * select field (e.g. when adding a GHS bank_transfer recipient payment
 * method). Each entry is { name, bank_code } — bank_code must be sent
 * back to createPaymentMethod exactly as returned (lowercase, e.g.
 * "gh_0022"); do not re-case or reformat it client-side.
 *
 * Reference data — doesn't change during a session, so no polling and a
 * long staleTime to avoid refetching every time the form mounts.
 */
export function usePayoutBanks(country) {
  const normalizedCountry = String(country ?? "").trim().toUpperCase();

  const banks =
    normalizedCountry.length === 2
      ? payoutBanks[normalizedCountry] ?? []
      : [];

  return {
    banks,
    isLoadingBanks: false,
    banksError: null,
  };
}

const EMPTY_CORRIDORS = [];

/**
 * usePayoutCoverage()
 *
 * Reads GET /me/corridors: the recipient countries the signed-in customer
 * can send to from their own country, and the payout channels each one
 * offers. The server computes these from each provider's legs, so this is
 * the single answer to "which countries and channels do we offer" — the
 * country picker, channel picker and bank lookup all derive from it.
 *
 * Until the query resolves (or if it fails) coverage is empty: nothing is
 * offered rather than a guess.
 *
 * Returns:
 *   coverage               { [countryCode]: [channel, ...] }
 *   supportedCountryCodes  [countryCode, ...]
 *   isChannelSupported     (countryCode, channel) => boolean, safe with
 *                          undefined/lowercase input
 */
export function usePayoutCoverage() {
  const corridorsQuery = useQuery({
    queryKey: ["me", "corridors"],
    queryFn: () => meService.listCorridors(),
    staleTime: 5 * 60 * 1000,
  });

  const corridors = corridorsQuery.data ?? EMPTY_CORRIDORS;

  const coverage = useMemo(
    () =>
      Object.fromEntries(
        corridors.map((corridor) => [
          String(corridor.recipient_country).toUpperCase(),
          corridor.payout_channels ?? [],
        ])
      ),
    [corridors]
  );

  const supportedCountryCodes = useMemo(
    () => Object.keys(coverage),
    [coverage]
  );

  const isChannelSupported = useCallback(
    (countryCode, channel) => {
      const channels =
        coverage[String(countryCode || "").toUpperCase()];

      return Array.isArray(channels) && channels.includes(channel);
    },
    [coverage]
  );

  return {
    coverage,
    supportedCountryCodes,
    isChannelSupported,
    isLoadingCoverage: corridorsQuery.isLoading,
    coverageError: corridorsQuery.error,
  };
}
